import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import { useLots } from './LotsContext';
import { useBusiness } from './BusinessContext';

const SalesContext = createContext();

export function SalesProvider({ children }) {
  const { user } = useAuth();
  const { business } = useBusiness();
  const { updateInventory, addLot } = useLots();
  const [sales, setSales] = useState([]);
  const [preparedItems, setPreparedItems] = useState({}); // { saleId: Set(itemIds) }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextDisplayId, setNextDisplayId] = useState(1);

  useEffect(() => {
    if (user && business?.id) {
      fetchSales();
    } else {
      setSales([]);
    }
  }, [user, business?.id]);

  const formatDisplayId = (num) => {
    return `V#${String(num).padStart(3, '0')}`;
  };

  const fetchSales = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: salesData, error: salesError } = await supabase
        .from('sales')
        .select(`
          *,
          client:clients (
            id,
            name,
            has_current_account,
            balance
          ),
          sale_items (
            id,
            lot_id,
            quantity,
            unit_price,
            deposit_amount,
            deposit_quantity,
            subtotal,
            is_lightning_lot,
            title,
            lot:lots (
              name,
              unit_sign,
              display_id,
              status,
              initial_quantity,
              needs_reconciliation
            )
          )
        `)
        .eq('auth_user_id', user.id)
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });

      // Log para verificar cuántas ventas se recuperaron
      console.log(`Ventas recuperadas para el negocio ${business.id}: ${salesData?.length || 0}`);

      console.log('=== DEBUG VENTAS RELÁMPAGO ===');
      if (salesData && salesData.length > 0) {
        const firstSale = salesData[0];
        console.log('Primera venta:', {
          id: firstSale.id,
          items: firstSale.sale_items.map(item => ({
            lot_id: item.lot_id,
            is_lightning: item.is_lightning_lot,
            lot_data: item.lot
          }))
        });

        // Verificar lotes específicos
        const lightningItems = firstSale.sale_items.filter(item => item.is_lightning_lot);
        if (lightningItems.length > 0) {
          console.log('Items relámpago encontrados:', lightningItems.map(item => ({
            lot_id: item.lot_id,
            lot_name: item.lot?.name,
            lot_status: item.lot?.status
          })));

          // Verificar lotes directamente
          const { data: lotsData } = await supabase
            .from('lots')
            .select('*')
            .in('id', lightningItems.map(item => item.lot_id));
          
          console.log('Datos de lotes relámpago:', lotsData);
        }
      }

      if (salesError) throw salesError;

      // Transformar los datos para el formato que espera la UI
      const formattedSales = salesData.map(sale => ({
        id: sale.id,
        display_id: sale.display_id,
        customer_name: sale.customer_name,
        client: sale.client ? {
          id: sale.client.id,
          name: sale.client.name,
          hasCurrentAccount: sale.client.has_current_account,
          balance: sale.client.balance
        } : null,
        total_amount: sale.total_amount,
        total_deposit_amount: sale.total_deposit_amount,
        deposit_quantity: sale.deposit_quantity,
        status: sale.status,
        payment_status: sale.payment_status,
        delivery_status: sale.delivery_status,
        created_at: sale.created_at,
        items: sale.sale_items.map(item => ({
          id: item.id,
          lot_id: item.lot_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          deposit_amount: item.deposit_amount,
          deposit_quantity: item.deposit_quantity,
          subtotal: item.subtotal,
          lotId: item.lot_id,
          unitPrice: item.unit_price,
          deposit: item.deposit_amount,
          totalDeposit: item.deposit_amount * item.quantity,
          title: item.title || item.lot?.name || item.lot?.title,
          unit: item.lot?.unit_sign,
          is_lightning_lot: item.is_lightning_lot,
          lot: {
            ...item.lot,
            display_id: item.lot?.display_id
          }
        }))
      }));

      setSales(formattedSales);

      // Actualizar el nextDisplayId
      const maxDisplayId = formattedSales.reduce((max, sale) => {
        const match = sale.display_id?.match(/V#(\d+)/);
        const num = match ? parseInt(match[1]) : 0;
        return Math.max(max, num);
      }, 0);

      setNextDisplayId(maxDisplayId + 1);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setError(error.message);
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const getSale = (id) => {
    return sales.find(sale => sale.id === id);
  };

  const addSale = async (saleData) => {
    try {
      if (!user || !business?.id) {
        throw new Error('Debes estar logueado y tener un negocio seleccionado para crear una venta');
      }

      // Crear lotes relámpago si es necesario
      const processedItems = await Promise.all(saleData.items.map(async (item) => {
        if (!item.lotId) {
          console.log('=== DEBUG CREACIÓN LOTE RELÁMPAGO ===');
          console.log('Item original:', item);
          console.log('Datos para crear lote:', {
            name: item.title,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            deposit: item.deposit
          });
          // Crear un lote relámpago
          const lightningLot = await addLot({
            name: item.title,
            quantity: item.quantity,
            initial_quantity: item.quantity,
            remaining_quantity: 0,
            unit: item.unit,
            unit_sign: item.unit,
            unitPrice: item.unitPrice,
            container_deposit: item.deposit || 0,
            status: 'lightning',
            needs_reconciliation: true,
            business_id: business.id
          });

          console.log('Lote relámpago creado:', lightningLot);
          console.log('ID del lote relámpago:', lightningLot.id);

          // Retornar el item con el nuevo lotId y marcar como lightning
          const processedItem = {
            ...item,
            lotId: lightningLot.id,
            lot_id: lightningLot.id,
            is_lightning_lot: true,
            title: item.title
          };
          
          console.log('Item procesado con IDs:', {
            lotId: processedItem.lotId,
            lot_id: processedItem.lot_id,
            title: processedItem.title
          });

          return processedItem;
        }
        return {
          ...item,
          lot_id: item.lotId,
          is_lightning_lot: false
        };
      }));

      console.log('Items procesados antes de crear la venta:', processedItems);

      // Calcular totales con los items procesados
      const total_amount = processedItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
      const total_deposit_amount = processedItems.reduce((sum, item) => sum + (item.deposit * item.quantity), 0);

      // Preparar datos de la venta
      const salePayload = {
        auth_user_id: user.id,
        business_id: business.id,
        customer_name: saleData.client?.name || null,
        client_id: saleData.client?.id || null,
        total_amount,
        total_deposit_amount,
        deposit_quantity: processedItems.reduce((sum, item) => sum + item.quantity, 0),
        status: saleData.status || 'confirmed',
        payment_status: 'unpaid',
        delivery_status: 'undelivered',
        created_at: new Date().toISOString()
      };

      console.log('Creando venta con:', salePayload);

      // Insertar la venta
      const { data: newSale, error: saleError } = await supabase
        .from('sales')
        .insert([salePayload])
        .select('*')
        .single();

      if (saleError) {
        console.error('Error al crear venta:', saleError);
        throw saleError;
      }

      console.log('Venta creada:', newSale);

      // Insertar los items de la venta
      const itemsPayload = processedItems.map(item => {
        console.log('Preparando item para inserción:', {
          lotId: item.lotId,
          lot_id: item.lot_id,
          title: item.title
        });
        
        return {
          sale_id: newSale.id,
          lot_id: item.lotId,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          deposit_amount: item.deposit || 0,
          deposit_quantity: item.quantity,
          subtotal: item.quantity * item.unitPrice,
          is_lightning_lot: item.is_lightning_lot,
          title: item.title
        };
      });

      const { data: saleItems, error: itemsError } = await supabase
        .from('sale_items')
        .insert(itemsPayload)
        .select(`
          *,
          lot:lots (
            name,
            unit_sign
          )
        `);

      if (itemsError) {
        console.error('Error al crear items:', itemsError);
        throw itemsError;
      }

      console.log('Items de venta creados:', saleItems);

      // Actualizar inventario para cada item
      for (const item of processedItems) {
        await updateInventory(item.lotId, item.quantity, 'remove', `Venta #${newSale.display_id}`);
      }

      // Actualizar estado local
      const formattedSale = {
        ...newSale,
        items: saleItems.map(item => ({
          id: item.id,
          lot_id: item.lot_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          deposit_amount: item.deposit_amount,
          deposit_quantity: item.deposit_quantity,
          subtotal: item.subtotal,
          lotId: item.lot_id,
          unitPrice: item.unit_price,
          deposit: item.deposit_amount,
          totalDeposit: item.deposit_amount * item.quantity,
          title: item.title || item.lot?.name || item.lot?.title,
          unit: item.lot.unit_sign,
          is_lightning_lot: item.is_lightning_lot
        }))
      };

      setSales(current => [formattedSale, ...current]);

      return formattedSale;
    } catch (error) {
      console.error('Error adding sale:', error);
      throw error;
    }
  };

  const updateSale = async (id, updateData) => {
    try {
      console.log('Actualizando venta con datos:', updateData);
      
      // Primero refrescamos el caché del esquema
      await supabase.rpc('refresh_schema_cache');
      
      // Luego actualizamos la venta
      const { data: updatedSale, error: updateError } = await supabase
        .from('sales')
        .update({
          status: updateData.status,
          payment_status: updateData.payment_status,
          delivery_status: updateData.delivery_status,
          delivered_at: updateData.delivered_at,
          delivered_by: updateData.delivered_by,
          is_delivered: updateData.delivery_status === 'delivered',
          customer_name: updateData.client?.name || null,
          client_id: updateData.client?.id || null,
          payments: updateData.payments,
          paid_at: updateData.paid_at,
          paid_by: updateData.paid_by || null
        })
        .eq('id', id)
        .select(`
          *,
          sale_items (
            *,
            lot:lots (
              name,
              unit_sign,
              display_id,
              status
            )
          ),
          client:clients (*)
        `)
        .single();

      if (updateError) {
        console.error('Error de Supabase al actualizar:', updateError);
        throw updateError;
      }

      // Paso 2: Formatear la venta para el estado local
      const formattedSale = {
        ...updatedSale,
        items: updatedSale.sale_items.map(item => ({
          id: item.id,
          lot_id: item.lot_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          deposit_amount: item.deposit_amount,
          deposit_quantity: item.deposit_quantity,
          subtotal: item.subtotal,
          lotId: item.lot_id,
          unitPrice: item.unit_price,
          deposit: item.deposit_amount,
          totalDeposit: item.deposit_amount * item.quantity,
          title: item.title || item.lot?.name || item.lot?.title,
          unit: item.lot.unit_sign,
          is_lightning_lot: item.is_lightning_lot
        }))
      };

      // Paso 3: Actualizar el estado local
      setSales(prevSales => 
        prevSales.map(s => 
          s.id === id ? formattedSale : s
        )
      );

      return formattedSale;
    } catch (error) {
      console.error('Error updating sale:', error);
      throw error;
    }
  };

  const updatePreparedItems = (saleId, itemId, isPrepared) => {
    setPreparedItems(prev => {
      const saleItems = prev[saleId] ? new Set(prev[saleId]) : new Set();
      
      if (isPrepared) {
        saleItems.add(itemId);
      } else {
        saleItems.delete(itemId);
      }

      return {
        ...prev,
        [saleId]: saleItems
      };
    });
  };

  const getPreparedItems = (saleId) => {
    return preparedItems[saleId] || new Set();
  };

  const cancelSale = async (id, cancellationInfo) => {
    try {
      const sale = sales.find(s => s.id === id);
      if (!sale) throw new Error('Venta no encontrada');
      
      console.log('Cancelando venta:', id, 'con información:', cancellationInfo);
      
      // 1. Obtener información completa de la venta
      const { data: saleData, error: saleError } = await supabase
        .from('sales')
        .select(`
          *,
          sale_items (
            *,
            lot:lots (
              name,
              unit_sign,
              display_id,
              status
            )
          )
        `)
        .eq('id', id)
        .single();
      
      if (saleError) throw saleError;
      
      // 2. Actualizar la venta en la base de datos
      const { data: updatedSale, error: updateError } = await supabase
        .from('sales')
        .update({
          status: 'canceled'
        })
        .eq('id', id)
        .select('*')
        .single();
      
      if (updateError) throw updateError;
      
      // 3. Revertir cambios en el inventario
      for (const item of saleData.sale_items) {
        // Solo revertir si el lote existe y no es un lote relámpago
        if (item.lot && item.lot.status !== 'lightning') {
          await updateInventory(
            item.lot_id, 
            item.quantity, 
            'add', 
            `Cancelación de venta #${sale.display_id}`
          );
        }
      }
      
      // 4. Actualizar el estado local
      setSales(prevSales => 
        prevSales.map(s => 
          s.id === id ? { ...s, status: 'canceled' } : s
        )
      );
      
      // Log para confirmar la actualización
      console.log('Venta actualizada a estado canceled:', updatedSale);
      
      return updatedSale;
    } catch (error) {
      console.error('Error canceling sale:', error);
      throw error;
    }
  };

  const value = {
    sales,
    loading,
    error,
    addSale,
    updateSale,
    cancelSale,
    getSale,
    refreshSales: fetchSales,
    updatePreparedItems,
    getPreparedItems
  };

  return (
    <SalesContext.Provider value={value}>
      {children}
    </SalesContext.Provider>
  );
}

export const useSales = () => {
  const context = useContext(SalesContext);
  if (!context) {
    throw new Error('useSales must be used within a SalesProvider');
  }
  return context;
}; 